import { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { useManualQuery } from '@zenbusiness/application-commons-graphql';
import { camelCase } from 'lodash';

import { DB_APPLICATION_STATUS } from '#client/constants/application';
import { ID_THEFT_SCORE } from '#client/constants/astra';
import { useLogError } from '#client/hooks/useLogError';
import useMemoObject from '#client/hooks/useMemoObject';
import { useInitialDataContext } from '#client/InitialDataContext';
import { GET_APPLICATION_DOCS_STATUS_QUERY } from '#client/queries';

import useConstBusinessEntityUuid from './useConstBusinessEntityUuid';

export const ApplicationStatusContext = createContext();
ApplicationStatusContext.displayName = 'ApplicationStatusContext';

const useApplicationStatusContext = () => {
  const context = useContext(ApplicationStatusContext);

  if (typeof context === 'undefined') {
    throw new Error(
      'useApplicationStatusContext must be used within a ApplicationStatusContext.Provider'
    );
  }

  return context;
};

export function ApplicationStatusProvider({ children }) {
  const { businessEntityUuid } = useConstBusinessEntityUuid();
  const [isProcessing, setIsProcessing] = useState(true);
  const {
    loading: fetchingAppStatus,
    data: originalData,
    error: appStatusError,
    errors: appStatusErrors,
    refetch: refetchAppStatus
  } = useInitialDataContext();

  const data = useMemoObject(originalData);

  useLogError(appStatusErrors, appStatusError);

  const [
    getAppDocsStatus,
    { data: applicationDocsStatusData, error: docStatusError, errors: docStatusErrors }
  ] = useManualQuery(GET_APPLICATION_DOCS_STATUS_QUERY, {
    variables: {
      input: {
        businessUuid: businessEntityUuid
      }
    },
    skip: !businessEntityUuid,
    skipCache: true,
    noThrow: true
  });
  useLogError(docStatusErrors, docStatusError);
  const appStatus = camelCase(data?.bankingApplication?.status) ?? null; // Remove camelCase when we update application init
  const applicationDataId = data?.bankingApplication?.applicationDataId ?? null;
  const applicationId = data?.bankingApplication?.applicationId ?? null;
  const initialPaymentStatus = data?.bankingApplication?.initialPaymentStatus ?? null;
  const idTheftScore = data?.bankingApplication?.idTheftScore ?? ID_THEFT_SCORE.MAX_SCORE; // default value 1000 will be ensure hide InsD
  const docsStatus = applicationDocsStatusData?.application?.status ?? null;
  const isApprovedStatus = appStatus === DB_APPLICATION_STATUS.APPROVED;
  const isInvalidStatus = !!appStatus && appStatus !== DB_APPLICATION_STATUS.APPROVED;

  useEffect(() => {
    const appStatusIsWaitingDocs =
      !fetchingAppStatus && appStatus === DB_APPLICATION_STATUS.AWAITING_DOCUMENTS;
    if (!appStatusIsWaitingDocs) {
      // force end processing, no need call API docs status anymore
      setIsProcessing(false);
      return;
    }

    // call API get docs status when invalid condition
    if (appStatusIsWaitingDocs) {
      getAppDocsStatus().then(() => {
        setIsProcessing(false);
      });
    }
    // eslint-disable-nex-line react-hooks/exhaustive-deps
  }, [fetchingAppStatus, appStatus, getAppDocsStatus]);

  const value = useMemo(
    () => ({
      applicationDataId,
      applicationId,
      appStatus,
      initialPaymentStatus,
      idTheftScore,
      appStatusError,
      docsStatus,
      docStatusError,
      isProcessing,
      isApprovedStatus,
      isInvalidStatus,
      loading: fetchingAppStatus,
      refetchAppDocsStatus: getAppDocsStatus,
      refetchAppStatus
    }),
    [
      applicationDataId,
      applicationId,
      appStatus,
      idTheftScore,
      initialPaymentStatus,
      appStatusError,
      docStatusError,
      docsStatus,
      fetchingAppStatus,
      isApprovedStatus,
      isInvalidStatus,
      getAppDocsStatus,
      isProcessing,
      refetchAppStatus
    ]
  );

  return (
    <ApplicationStatusContext.Provider value={value}>{children}</ApplicationStatusContext.Provider>
  );
}

export default useApplicationStatusContext;
